import React, { useState, useCallback, ReactNode, useRef, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { convertFileToBase64 } from '../../../../utils/fileConverter'

import CreatorAccordion from '../../../ui_reusable/CreatorAccordion'
import VersatileGrid from '../../../ui_reusable/VersatileGrid'
import CustomFileUploader, {
  FileData,
} from '../../../ui_reusable/CustomFileUploader'

import CustomIcons from '../../../ui_reusable/CustomIcons'
import CheckmarkAnimation from '../../../ui_reusable/CheckmarkAnimation'
import { Container } from '../../../ui_reusable/styles/CreateInputUtils.styles'
import {
  FormGroup,
  Label,
  ButtonContainer,
  ErrorMessage,
  SuccessContainer,
  ServerErrorMessage,
} from '../../../ui_reusable/styles/CreateInputUtils.styles'
import {
  VStack,
  SelectorHStack,
  RequiredLabelStar,
} from './dataset_form/styles/DatasetForm.styles'
import {
  ModalButtonCancel,
  StandardButton,
} from '../../../ui_reusable/styles/Button.styles'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
  StatsContainer,
} from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import { EasyIconButton } from '../../../ui_reusable/styles/Button.styles'
import { SmallNormalTag } from '../../../ui_reusable/styles/Tags.styles'
import { HeaderMedium600Style } from '../../../../styles/Text.styles'
import theme from '../../../../styles/theme'
import {
  useDatasetsPreloadCreate,
  useDatasetsUploadCreate,
} from '../../../../api/generated/endpoints/databook/databook'
import StatusTagWithTooltip, {
  UploadStatus,
} from '../../../ui_reusable/StatusTagWithTooltip'
import ShowDatasetDropdown from '../../../comp_reusable/datasets/ShowDatasetDropdown'
import Modal from '../../../ui_reusable/Modal'
import { Button } from 'rsuite'

import type {
  Databook,
  DatabookPreload,
  Region,
  RegionDatabook,
} from '../../../../api/generated/models'

// Extended type for grid display with hierarchy markers
interface ExtendedPreloadRow {
  _searchId: string
  _rowType: 'channel' | 'dataset' | 'regional_variant'
  _level: number
  _status: UploadStatus
  _isNew: boolean // Whether the dataset is new (vs update)
  _errors?: string[] | null
  _currentObservations?: number // For showing existing observations on updates
  // Dataset/Channel fields (may be undefined for error rows)
  name: string
  category?: string | null
  interval?: string | null
  end_at?: string | null
  total_observations?: number | null
  quality?: string | null
  id?: number | string | null // Can be number (dataset) or string|null (channel)
  units?: string | null
  // Spread remaining Dataset fields
  [key: string]: any
}

interface UploadFormData {
  file: FileData | null
  granularity: 'daily' | 'weekly' | 'monthly' | ''
  market_ids: number[]
}

const acceptedFileTypes =
  '.xls, .xlsx, application/excel, application/vnd.msexcel, ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// Helper function to determine status from dataset
const getDatasetStatus = (dataset: Databook | RegionDatabook): UploadStatus => {
  // Check for errors first (including quality issues)
  if (dataset.errors && dataset.errors.length > 0) return 'error'
  // status.new determines if it's new or update
  return dataset.status?.new ? 'new' : 'update'
}

// Headers configuration for the VersatileGrid
const headers = [
  {
    title: 'Driver name',
    key: 'name',
    type: 'string' as const,
    isSortable: true,
    isSearchable: true,
    flexGrow: 2,
  },
  {
    title: 'Type',
    key: 'category',
    type: 'string' as const,
    isSortable: true,
    isSearchable: true,
  },
  {
    title: 'Granularity',
    key: 'interval',
    type: 'string' as const,
    isSortable: true,
    isSearchable: true,
    center: true,
  },
  {
    title: 'Last observation',
    key: 'end_at',
    type: 'datetime' as const,
    isSortable: true,
    isSearchable: true,
  },
  {
    title: 'Observations',
    key: 'total_observations',
    type: 'number' as const,
    isSortable: true,
    isSearchable: true,
    center: true,
  },
  {
    title: 'Status',
    key: '_status',
    type: 'string' as const,
    isSortable: true,
    isSearchable: true,
    center: true,
  },
  {
    title: 'View/Delete',
    key: 'actions',
    type: 'none' as any,
    isSortable: false,
    isSearchable: false,
    center: true,
  },
]

function UploadDataset() {
  const panelRef = useRef<any>(null)
  const customFileUploaderRef = useRef<any>(null)

  const methods = useForm<UploadFormData>({
    defaultValues: {
      file: null,
    },
  })

  const {
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = methods

  // Local state
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [preloadResponse, setPreloadResponse] =
    useState<DatabookPreload | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [selectedDataset, setSelectedDataset] =
    useState<ExtendedPreloadRow | null>(null)
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const preloadDatasets = useDatasetsPreloadCreate()
  const uploadDatasets = useDatasetsUploadCreate()

  // Process API response into flat data with hierarchy markers
  const processedData = useMemo((): ExtendedPreloadRow[] => {
    if (!preloadResponse) return []

    const flatData: ExtendedPreloadRow[] = []

    // Process media_channels with hierarchy
    preloadResponse.media_channels.forEach((channel) => {
      // Add channel row
      flatData.push({
        ...channel,
        name: channel.name || '',
        _searchId: `channel-${channel.id}`,
        _rowType: 'channel',
        _level: 0,
        _status: 'new', // Channels in upload context are implicitly new
        _isNew: true,
      })

      // Add nested datasets under the channel
      channel.datasets?.forEach((dataset) => {
        const status = getDatasetStatus(dataset)
        const isNew = dataset.status?.new ?? true
        flatData.push({
          ...dataset,
          name: dataset.name || '',
          _searchId: `dataset-${dataset.id}`,
          _rowType: 'dataset',
          _level: 1,
          _status: status,
          _isNew: isNew,
          _errors: dataset.errors,
          _currentObservations: dataset.status?.current_observations,
        })

        // Add nested regional variants under the dataset
        const hasRegionalVariants =
          // @ts-ignore
          dataset.regional_variants && dataset.regional_variants.length > 0
        if (hasRegionalVariants) {
          // @ts-ignore
          dataset.regional_variants?.forEach(
            (regionalDataset: RegionDatabook) => {
              const regionalStatus = getDatasetStatus(regionalDataset)
              const regionalIsNew = regionalDataset.status?.new ?? true
              // Use segment_name if available, otherwise fall back to region.name
              const displayName =
                (regionalDataset as any).segment_name ||
                regionalDataset.region?.name ||
                'Unknown Region'

              flatData.push({
                ...regionalDataset,
                name: displayName,
                category: 'Segment',
                _searchId: `regional-${regionalDataset.id}`,
                _rowType: 'regional_variant',
                _level: 2,
                _status: regionalStatus,
                _isNew: regionalIsNew,
                _errors: regionalDataset.errors,
                _currentObservations:
                  regionalDataset.status?.current_observations,
              })
            },
          )
        }
      })
    })

    // Process datasets_without_channel
    preloadResponse.datasets_without_channel.forEach((dataset) => {
      const status = getDatasetStatus(dataset)
      const isNew = dataset.status?.new ?? true
      const hasRegionalVariants =
        // @ts-ignore
        dataset.regional_variants && dataset.regional_variants.length > 0

      flatData.push({
        ...dataset,
        name: dataset.name || '',
        _searchId: `dataset-${dataset.id}`,
        _rowType: 'dataset',
        _level: 0,
        _status: status,
        _isNew: isNew,
        _errors: dataset.errors,
        _currentObservations: dataset.status?.current_observations,
      })

      // Add nested regional variants under the dataset
      if (hasRegionalVariants) {
        // @ts-ignore
        dataset.regional_variants?.forEach(
          (regionalDataset: RegionDatabook) => {
            const regionalStatus = getDatasetStatus(regionalDataset)
            const regionalIsNew = regionalDataset.status?.new ?? true
            // Use segment_name if available, otherwise fall back to region.name
            const displayName =
              (regionalDataset as any).segment_name ||
              regionalDataset.region?.name ||
              'Unknown Region'

            flatData.push({
              ...regionalDataset,
              name: displayName,
              category: 'Segment',
              _searchId: `regional-${regionalDataset.id}`,
              _rowType: 'regional_variant',
              _level: 1,
              _status: regionalStatus,
              _isNew: regionalIsNew,
              _errors: regionalDataset.errors,
              _currentObservations:
                regionalDataset.status?.current_observations,
            })
          },
        )
      }
    })

    return flatData
  }, [preloadResponse])

  // Calculate stats for header (includes datasets and regional variants)
  const stats = useMemo(() => {
    const countableRows = processedData.filter(
      (d) => d._rowType === 'dataset' || d._rowType === 'regional_variant',
    )
    return {
      total: countableRows.length,
      new: countableRows.filter((d) => d._status === 'new').length,
      update: countableRows.filter((d) => d._status === 'update').length,
      error: countableRows.filter((d) => d._status === 'error').length,
    }
  }, [processedData])

  // Check if all drivers are correct (no errors)
  const allDriversCorrect = useMemo(() => {
    return stats.error === 0
  }, [stats])

  // Check if there's any valid data to save
  const hasValidData = useMemo(() => {
    if (!preloadResponse) return false
    return (
      preloadResponse.media_channels.length > 0 ||
      preloadResponse.datasets_without_channel.length > 0
    )
  }, [preloadResponse])

  // Can save only if there's valid data AND all drivers are correct
  const canSave = hasValidData && allDriversCorrect

  // Grid header with stats
  const gridHeader: ReactNode = useMemo(() => {
    if (!preloadResponse) return null

    return (
      <StatsContainer>
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <span css={HeaderMedium600Style}>
            Total drivers identified: {stats.total}
          </span>
          <div style={{ display: 'flex', justifyItems: 'center' }}>
            <SmallNormalTag>{stats.new} new</SmallNormalTag>
            <SmallNormalTag>{stats.update} update</SmallNormalTag>
            <SmallNormalTag>{stats.error} error</SmallNormalTag>
          </div>
        </div>
      </StatsContainer>
    )
  }, [preloadResponse, stats])

  // Handlers
  const handleFileChange = (files: FileData[]) => {
    if (files && files.length > 0) {
      setValue('file', files[0])
      clearErrors('file')
    } else {
      setValue('file', null)
    }
  }

  const handleFileRemove = () => {
    setValue('file', null)
    setPreloadResponse(null) // Clear preload response when file is removed
    setServerError(null)
  }

  const handleExpand = useCallback((dataIdx: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(dataIdx)) {
        next.delete(dataIdx)
      } else {
        next.add(dataIdx)
      }
      return next
    })
  }, [])

  const handleDelete = useCallback((row: ExtendedPreloadRow): void => {
    setSelectedDataset(row)
    setDeleteModalOpen(true)
  }, [])

  const handleCloseDeleteModal = (): void => {
    setDeleteModalOpen(false)
    setSelectedDataset(null)
  }

  const handleConfirmDelete = (): void => {
    if (!selectedDataset || !preloadResponse) {
      handleCloseDeleteModal()
      return
    }

    const rowType = selectedDataset._rowType
    const rowName = selectedDataset.name

    setPreloadResponse((prev) => {
      if (!prev) return prev

      if (rowType === 'dataset') {
        // Delete a dataset (and its children) from media_channels or datasets_without_channel
        const updatedMediaChannels = prev.media_channels.map((channel) => ({
          ...channel,
          datasets: channel.datasets?.filter((d) => d.name !== rowName),
        }))

        const updatedDatasetsWithoutChannel =
          prev.datasets_without_channel.filter((d) => d.name !== rowName)

        return {
          ...prev,
          media_channels: updatedMediaChannels,
          datasets_without_channel: updatedDatasetsWithoutChannel,
        }
      }

      if (rowType === 'regional_variant') {
        // Delete a regional variant from its parent dataset
        const updatedMediaChannels = prev.media_channels.map((channel) => ({
          ...channel,
          datasets: channel.datasets?.map((dataset) => ({
            ...dataset,
            // @ts-ignore - regional_variants exists on the preload response
            regional_variants: dataset.regional_variants?.filter(
              (rv: RegionDatabook) => {
                const rvDisplayName =
                  (rv as any).segment_name ||
                  rv.region?.name ||
                  'Unknown Region'
                return rvDisplayName !== rowName
              },
            ),
          })),
        }))

        const updatedDatasetsWithoutChannel = prev.datasets_without_channel.map(
          (dataset) => ({
            ...dataset,
            // @ts-ignore - regional_variants exists on the preload response
            regional_variants: dataset.regional_variants?.filter(
              (rv: RegionDatabook) => {
                const rvDisplayName =
                  (rv as any).segment_name ||
                  rv.region?.name ||
                  'Unknown Region'
                return rvDisplayName !== rowName
              },
            ),
          }),
        )

        return {
          ...prev,
          media_channels: updatedMediaChannels,
          datasets_without_channel: updatedDatasetsWithoutChannel,
        }
      }

      // For other row types (e.g., 'channel'), return unchanged
      return prev
    })

    handleCloseDeleteModal()
  }

  const handleUploadDatabook = async (formData: UploadFormData) => {
    // Validate required fields
    let hasErrors = false

    if (!formData.file) {
      setError('file', { type: 'required', message: 'File is required' })
      hasErrors = true
    }

    if (hasErrors) return

    setIsUploading(true)
    setPreloadResponse(null) // Clear previous response

    try {
      const fileData = await convertFileToBase64(formData.file!.blobFile)

      const response = await preloadDatasets.mutateAsync({
        data: { file: fileData },
      })

      if (response.data) {
        setPreloadResponse(response.data)
      }
    } catch (error) {
      console.error('Error uploading databook:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSaveDatabook = async () => {
    if (!preloadResponse) return

    setIsSaving(true)
    setServerError(null)
    try {
      // Pass the preload response to the upload endpoint
      await uploadDatasets.mutateAsync({
        data: preloadResponse as any,
      })

      // Show success animation
      setShowSuccess(true)

      // Clear state on success
      setPreloadResponse(null)
      // Clear the file uploader
      if (customFileUploaderRef.current) {
        customFileUploaderRef.current.reset()
      }
      setValue('file', null)

      // Hide success after animation
      setTimeout(() => {
        setShowSuccess(false)
      }, 1500)

      // Clear server errors
      setTimeout(() => {
        setServerError(null)
      }, 2000)
    } catch (error: any) {
      console.error('Error saving databook:', error)

      // Extract error message for display
      let errorMessage = 'Save failed'

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500) - Check backend logs for details'
      } else if (error.response?.data?.detail) {
        errorMessage = String(error.response.data.detail)
      } else if (error.response?.data?.message) {
        errorMessage = JSON.stringify(error.response.data.message)
      } else if (error.response?.data) {
        // Pass through the full error object for field-level error mapping
        errorMessage = JSON.stringify(error.response.data)
      } else if (error.message) {
        errorMessage = String(error.message)
      }

      setServerError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setServerError(null)
    setIsSaving(false)
    setShowSuccess(false)
    setSelectedDataset(null)
    setPreloadResponse(null)
    setIsUploading(false)
    // Reset the file uploader
    if (customFileUploaderRef.current) {
      customFileUploaderRef.current.reset()
    }
    setValue('file', null)
  }

  // Render intersecting row (expandable chart view) for datasets and regional variants
  const renderIntersectingRow = useCallback(
    (rowData: any, rowIdx: number, dataIdx: number): ReactNode | null => {
      const row = rowData as ExtendedPreloadRow
      // Only show the dropdown for dataset/regional_variant rows when expanded (not for channels)
      if (
        (row._rowType !== 'dataset' && row._rowType !== 'regional_variant') ||
        !expandedRows.has(dataIdx)
      ) {
        return null
      }

      return (
        <ShowDatasetDropdown
          dataset={row as unknown as Databook}
          datasetId={row.id}
          units={row.units}
          isPreloaded={!row.id}
        />
      )
    },
    [expandedRows],
  )

  // Render row based on type (channel or dataset)
  const renderRow = useCallback(
    (rowData: any, rowIdx: number, dataIdx: number): ReactNode[] => {
      const row = rowData as ExtendedPreloadRow

      // Helper to render observations with current_observations for updates
      const renderObservations = () => {
        // Only show arrow notation for updates when values differ
        if (
          !row._isNew &&
          row._currentObservations !== undefined &&
          row._currentObservations !== row.total_observations
        ) {
          return (
            <span>
              {row._currentObservations} → {row.total_observations ?? '-'}
            </span>
          )
        }
        return <span>{row.total_observations ?? '-'}</span>
      }

      // Channel row
      if (row._rowType === 'channel') {
        return [
          <GridCellBoldText
            style={{
              padding: '0.5rem',
              width: '100%',
            }}
          >
            {row.name}
          </GridCellBoldText>,
          <GridCellNormalText>Channel</GridCellNormalText>,
          // Empty cells for remaining columns
          ...Array(headers.length - 3).fill(<></>),
          // Status column - channels don't have a status badge
          <></>,
        ]
      }

      // Helper for nested rows
      const renderNestedName = () => {
        const CellComponent =
          row._rowType === 'dataset' && row._level === 0
            ? GridCellBoldText
            : GridCellNormalText
        return (
          <CellComponent
            style={{ paddingLeft: `${row._level * 0.5}rem`, width: '100%' }}
          >
            {row._level > 0 && (
              <CustomIcons.CornerDownRight size="xs" clickDisabled />
            )}
            <span style={{ marginLeft: '0.5rem' }}>{row.name}</span>
          </CellComponent>
        )
      }

      // Dataset row
      if (row._rowType === 'dataset') {
        return [
          renderNestedName(),
          <GridCellNormalText>{row.category || '-'}</GridCellNormalText>,
          <GridCellNormalText>{row.interval || '-'}</GridCellNormalText>,
          <GridCellSmallFadedText>
            {row.end_at ? new Date(row.end_at).toISOString().slice(0, 10) : '-'}
          </GridCellSmallFadedText>,
          <GridCellNormalText>{renderObservations()}</GridCellNormalText>,
          <GridCellNormalText>
            <StatusTagWithTooltip
              status={row._status}
              isNew={row._isNew}
              currentObservations={row._currentObservations}
              importObservations={row.total_observations}
              errors={row._errors}
            />
          </GridCellNormalText>,
          // View/Delete actions
          <div
            style={{ display: 'flex', gap: '0px', justifyContent: 'center' }}
          >
            <EasyIconButton
              onClick={() => handleExpand(dataIdx)}
              data-testid={`expand-${row.name}`}
            >
              {expandedRows.has(dataIdx) ? (
                <CustomIcons.faEyeSlash
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              ) : (
                <CustomIcons.faEye
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              )}
            </EasyIconButton>
            <EasyIconButton
              onClick={() => handleDelete(row)}
              data-testid={`delete-${row.name}`}
            >
              <CustomIcons.TrashIcon
                color={theme.colors.veryFadedText}
                size="xs"
              />
            </EasyIconButton>
          </div>,
        ]
      }

      // Regional variant row
      if (row._rowType === 'regional_variant') {
        return [
          renderNestedName(),
          <GridCellNormalText>Segment</GridCellNormalText>,
          <GridCellNormalText>{row.interval || '-'}</GridCellNormalText>,
          <GridCellSmallFadedText>
            {row.end_at ? new Date(row.end_at).toISOString().slice(0, 10) : '-'}
          </GridCellSmallFadedText>,
          <GridCellNormalText>{renderObservations()}</GridCellNormalText>,
          <GridCellNormalText>
            <StatusTagWithTooltip
              status={row._status}
              isNew={row._isNew}
              currentObservations={row._currentObservations}
              importObservations={row.total_observations}
              errors={row._errors}
            />
          </GridCellNormalText>,
          // View/Delete actions
          <div
            style={{ display: 'flex', gap: '0px', justifyContent: 'center' }}
          >
            <EasyIconButton
              onClick={() => handleExpand(dataIdx)}
              data-testid={`expand-${row.name}`}
            >
              {expandedRows.has(dataIdx) ? (
                <CustomIcons.faEyeSlash
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              ) : (
                <CustomIcons.faEye
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              )}
            </EasyIconButton>
            <EasyIconButton
              onClick={() => handleDelete(row)}
              data-testid={`delete-${row.name}`}
            >
              <CustomIcons.TrashIcon
                color={theme.colors.veryFadedText}
                size="xs"
              />
            </EasyIconButton>
          </div>,
        ]
      }

      // Fallback (should not reach)
      return []
    },
    [expandedRows, handleExpand, handleDelete],
  )

  return (
    <>
      <CreatorAccordion id={'uploadDatasetAccordion'}>
        <CreatorAccordion.Panel ref={panelRef} header="Import Databook">
          <Container>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(handleUploadDatabook)}>
                <FormGroup>
                  <SelectorHStack
                    style={{ marginTop: 0, alignItems: 'flex-start' }}
                  >
                    {/* File Upload */}
                    <VStack style={{ flex: 0 }}>
                      <Label>
                        File
                        <RequiredLabelStar>*</RequiredLabelStar>
                      </Label>
                      <CustomFileUploader
                        ref={customFileUploaderRef}
                        onFileSelect={handleFileChange}
                        onRemove={handleFileRemove}
                        acceptedTypes={acceptedFileTypes}
                        showBtn={true}
                      />
                      <ErrorMessage>{errors.file?.message}</ErrorMessage>
                    </VStack>
                  </SelectorHStack>

                  <ButtonContainer style={{ marginTop: '1.5rem' }}>
                    <StandardButton type="submit" disabled={isUploading}>
                      {isUploading ? 'Uploading...' : 'Upload Databook'}
                    </StandardButton>
                  </ButtonContainer>
                </FormGroup>
              </form>
            </FormProvider>
          </Container>

          <Container>
            <VersatileGrid
              headers={headers}
              data={processedData}
              searchPlaceholder="Find driver"
              renderRow={renderRow}
              renderIntersectingRow={renderIntersectingRow}
              headerInfo={gridHeader}
              maxDisplayedItems={10}
              emptyStateMessage="Upload a databook to see drivers here"
              data-testid="upload-drivers-grid"
            />

            <ButtonContainer
              style={{
                marginTop: '1.5rem',
                gap: '10px',
                display: 'flex',
                flexDirection: 'row-reverse',
              }}
            >
              <StandardButton
                type="button"
                onClick={handleSaveDatabook}
                disabled={isSaving || !canSave}
              >
                {isSaving ? 'Saving...' : 'Save databook'}
              </StandardButton>
              <ModalButtonCancel type="button" onClick={handleCancel}>
                Cancel
              </ModalButtonCancel>
            </ButtonContainer>
          </Container>

          {(serverError || showSuccess) && (
            <SuccessContainer>
              {serverError ? (
                <ServerErrorMessage>{serverError}</ServerErrorMessage>
              ) : (
                <CheckmarkAnimation
                  show={showSuccess}
                  text="Databook saved successfully!"
                />
              )}
            </SuccessContainer>
          )}
        </CreatorAccordion.Panel>
      </CreatorAccordion>

      {selectedDataset && (
        <Modal
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          title={`Remove ${selectedDataset.name}`}
        >
          <Modal.Body>
            <p>Are you sure you want to remove this driver from the import?</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={handleConfirmDelete}
              appearance="primary"
              color="violet"
            >
              Yes
            </Button>
            <Button onClick={handleCloseDeleteModal} appearance="subtle">
              No
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default UploadDataset
